import environment from '../../../../../environment'
import { fetchWithToken } from '../../../../../shared/utils/api/api'
import MAP_CONFIG from '../map.config'
import type { ExtendedMapGroup } from '../../../../../shared/utils/map'
import type {
  GeoSearchFeature,
  GeoSearchProperties,
} from '../../../components/MapSearchResults/types'
import getDetailPageData from '../../../../../shared/utils/getDetailPageData'
import { DataSubtype, DataType } from '../../../../SearchPage/types'

// TODO: Replace this type with the 'LatLng' type from Leaflet.
interface Location {
  latitude: number
  longitude: number
}

const generateParams = (layer: ExtendedMapGroup, location: Location, zoom: number) =>
  new URLSearchParams({
    ...layer.detailParams,
    lat: location.latitude.toString(),
    lon: location.longitude.toString(),
    _fields: 'type,legenda',
    radius: layer.detailIsShape
      ? '0'
      : Math.round(2 ** (MAP_CONFIG.BASE_LAYER_OPTIONS.maxZoom - zoom) / 2).toString(),
  } as Record<string, string>)

const sortResults = (results: MapLayer[]) =>
  results.sort((a, b) => {
    if (a.detailIsShape && b.detailIsShape) {
      return a.distance - b.distance
    }
    if (!a.detailIsShape) {
      if (!b.detailIsShape) {
        return a.distance - b.distance
      }
      return -1
    }
    return 1
  })

export interface MapLayer extends Omit<GeoSearchProperties, 'type'> {
  subType: DataSubtype
  type: DataType
  detailIsShape?: boolean
  id: string
  legenda?: string
}

const retrieveLayers = (detailItems: GeoSearchFeature[], detailIsShape?: boolean): MapLayer[] =>
  detailItems.map((item) => {
    const { type, subtype, id } = getDetailPageData(item.properties.uri)

    return {
      detailIsShape,
      ...item.properties,
      id,
      type,
      subType: subtype,
      legenda: item.properties.legenda,
    }
  })

const fixBenkApi = (subtype?: string) => {
  const fixedTypes: { [key: string]: DataSubtype } = {
    kadastraleobjecten: DataSubtype.Object,
    kadastralesubjecten: DataSubtype.Subject,
  }

  return fixedTypes[subtype as string] ?? subtype
}

export default async function fetchNearestDetail(
  location: Location,
  layers: ExtendedMapGroup[],
  zoom: number,
) {
  const layerUrls = layers
    .filter((layer) => layer.detailUrl)
    .map((layer) => {
      const params = generateParams(layer, location, zoom)

      return `${environment.DSO_API_ROOT}${layer.detailUrl!}?${params.toString()}`
    })
  const layerPromises = layers.map(async (layer, index) => {
    if (layerUrls.indexOf(layerUrls[index]) === index) {
      const result = await fetchWithToken(layerUrls[index], undefined, { includeApiKey: false })

      return retrieveLayers(result.features, layer.detailIsShape ?? false)
    }

    return []
  })

  const results = sortResults((await Promise.all(layerPromises)).reduce((a, b) => [...a, ...b]))

  const stedelijkGebiedProjectOfBelang = results.filter(
    ({ subType }) => subType === DataSubtype.StedelijkGebiedProjectOfBelangen,
  )

  if (stedelijkGebiedProjectOfBelang.length > 0) {
    const activeLegendaItemss = layers.map((layer) => layer.title)
    const extraSubTypeMatch = stedelijkGebiedProjectOfBelang.find(
      (result) => result.legenda && activeLegendaItemss.includes(result.legenda),
    )
    return extraSubTypeMatch ?? stedelijkGebiedProjectOfBelang[0]
  }

  if (layers.find((layer) => layer?.layers?.includes('aantal_meldingen'))) {
    return {
      ...results[0],
      type: DataType.Indicatoren,
      subType: DataSubtype.Buurt,
    }
  }

  if ((results?.[0]?.type as string) === 'brk2') {
    return {
      ...results[0],
      subType: fixBenkApi(results[0].subType),
      type: DataType.BRK,
    }
  }

  return results[0] ?? null
}
